/**
 * Dossier en vragen (ontwerp 31, punt 7): balk "N van M velden" met het
 * procent in de balk; drie kolommen bekend, nog te vragen (de
 * eerstvolgende met het woord "volgende") en wil niet delen (met datum).
 * Per open veld twee kleine knoppen: "Gevraagd" zet de vraag in het
 * register zonder antwoord, "Wil niet delen" betekent nooit meer vragen.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { v6Api, type KlantVraagInvoer } from "../../api";
import { v6Sleutels } from "../../sleutels";
import { datumKort } from "../opmaak";
import type { KaartDossier, KaartVraag } from "../types";

function Wat({ vraag }: { vraag: KaartVraag }) {
  return (
    <span className="wat">
      {vraag.label}
      {vraag.huisdier_naam ? <small>{` · ${vraag.huisdier_naam}`}</small> : null}
    </span>
  );
}

function sleutel(v: KaartVraag): string {
  return `${v.veld}:${v.huisdier_id ?? ""}`;
}

export function Dossier({
  klantId,
  dossier,
  compact = false,
}: {
  klantId: string;
  dossier: KaartDossier;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const register = useMutation({
    mutationFn: (v: { vraag: KaartVraag; antwoord?: "wil_niet" }) => {
      const body: KlantVraagInvoer = { veld: v.vraag.veld };
      if (v.vraag.huisdier_id) body.huisdier_id = v.vraag.huisdier_id;
      if (v.antwoord) body.antwoord = v.antwoord;
      return v6Api.klantVraag(klantId, body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) }),
  });
  const bezig = register.isPending ? sleutel(register.variables.vraag) : null;

  const totaal = dossier.velden_totaal;
  const procent = totaal > 0 ? Math.round((dossier.velden_bekend / totaal) * 100) : 0;

  return (
    <div className={`kk-dossier${compact ? " compact" : ""}`}>
      <div className="kk-balkrij">
        <div
          className="kk-balk"
          role="meter"
          aria-label="Dossier"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={procent}
        >
          <i style={{ width: `${procent}%` }} />
          <span className="in num">{`${procent}%`}</span>
        </div>
        <span className="over num">{`${dossier.velden_bekend} van ${totaal} velden`}</span>
      </div>

      <div className="kk-dossier-kol">
        <section aria-label="Bekend">
          <h4>Bekend</h4>
          {dossier.bekend.length === 0 ? (
            <p className="stil">geen</p>
          ) : (
            <ul>
              {dossier.bekend.map((v) => (
                <li key={sleutel(v)}>
                  <Wat vraag={v} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Nog te vragen">
          <h4>Nog te vragen</h4>
          {dossier.nog_te_vragen.length === 0 ? (
            <p className="stil">geen</p>
          ) : (
            <ul>
              {dossier.nog_te_vragen.map((v) => (
                <li key={sleutel(v)} className={v.eerstvolgende ? "volgende" : undefined}>
                  <span className="kop">
                    <Wat vraag={v} />
                    {v.eerstvolgende ? <span className="kk-label volgende">volgende</span> : null}
                  </span>
                  {v.gevraagd_op ? <small className="bron">{`gevraagd ${datumKort(v.gevraagd_op)}`}</small> : null}
                  <span className="kk-mini">
                    <button
                      type="button"
                      disabled={bezig === sleutel(v)}
                      aria-label={`${v.label}${v.huisdier_naam ? ` ${v.huisdier_naam}` : ""}: gevraagd`}
                      onClick={() => register.mutate({ vraag: v })}
                    >
                      Gevraagd
                    </button>
                    <button
                      type="button"
                      disabled={bezig === sleutel(v)}
                      aria-label={`${v.label}${v.huisdier_naam ? ` ${v.huisdier_naam}` : ""}: wil niet delen`}
                      onClick={() => register.mutate({ vraag: v, antwoord: "wil_niet" })}
                    >
                      Wil niet delen
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Wil niet delen">
          <h4>Wil niet delen</h4>
          {dossier.wil_niet.length === 0 ? (
            <p className="stil">geen</p>
          ) : (
            <ul>
              {dossier.wil_niet.map((v) => (
                <li key={sleutel(v)}>
                  <Wat vraag={v} />
                  {v.datum ? <small className="bron">{datumKort(v.datum)}</small> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {register.isError ? (
        <p className="v6-fout" role="alert">
          Vastleggen lukte niet. Probeer het opnieuw.
        </p>
      ) : null}
    </div>
  );
}
